import { AbsenceOrigin } from '@bp/bp-graphql-types';
import {
  Button,
  DatePicker,
  DotsVerticalIcon,
  Dropdown,
  DropdownMenu,
  Row,
  Table,
  type TableColumns,
  Tooltip,
  useDefaultSelecting,
  useDefaultSorting,
} from '@bp/ui-components';
import { type FragmentType, getFragmentData, graphql } from 'client/gql';
import { getAbsenceOriginTranslation } from 'components/Absences/utils/absenceUtils';
import { OrganizationConfigContext } from 'context/OrganizationConfigContext';
import dayjs from 'dayjs';
import { useIsDesktop } from 'hooks/useIsDesktop';
import { usePermissionChecker } from 'hooks/usePermissionChecker';
import { useContext, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { naturalStringSort } from 'utils/stringHelper';
import { onlyUnique } from '../../../utils/uniquByUuid';
import styles from './AbsenceAdministrationTable.module.scss';

const AbsenceAdministrationTableFragment = graphql(`
  fragment AbsenceAdministrationTableFields on Query {
    absences(where: $absencesWhere, options: { sort: { startTime: DESC } }) {
      __typename
      uuid
      owner {
        __typename
        uuid
      }
      reason {
        __typename
        uuid
        reason
      }
      startTime
      endTime
      comment
      excused
      origin
      originUuid
    }
    profiles(where: $profilesWhere, options: { sort: { displayName: ASC } }) {
      __typename
      uuid
      organizationRoles
      displayName
    }
    classes(where: $classesWhere, options: { sort: { name: ASC } }) {
      __typename
      uuid
      membersConnection {
        __typename
        edges {
          __typename
          node {
            __typename
            uuid
          }
        }
      }
      name
      shortName
    }
  }
`);

type AbsenceAdministrationTableProps = {
  isLoading: boolean;
  fromDate: Date;
  untilDate: Date;
  onFromChange: (date: Date) => void;
  onUntilChange: (date: Date) => void;
  onAdd: () => void;
  onEdit: (uuid: string) => void;
  onExcuse: (uuids: string[], isExcused: boolean) => void;
  onDelete: (uuids: string[]) => void;
  data: FragmentType<typeof AbsenceAdministrationTableFragment> | undefined;
};

type AbsenceAdministrationTableType = {
  uuid: string;
  name: string;
  classes: { name: string; shortName: string }[];
  reason: string;
  from: string;
  until: string;
  comment: string;
  isExcused: boolean;
  ownerUuid: string;
  origin: AbsenceOrigin;
  originUuid: string;
};

export const AbsenceAdministrationTable = ({
  isLoading,
  fromDate,
  untilDate,
  onFromChange,
  onUntilChange,
  onAdd,
  onEdit,
  onExcuse,
  onDelete,
  data,
}: AbsenceAdministrationTableProps) => {
  const { t } = useTranslation();
  const { currentSchoolYear } = useContext(OrganizationConfigContext);
  const isDesktop = useIsDesktop();

  const { rowSelection, onRowSelectionChange, clearSelection } = useDefaultSelecting();

  const fragment = getFragmentData(AbsenceAdministrationTableFragment, data);
  const permissions = usePermissionChecker();

  const absences = fragment?.absences ?? [];
  const profiles = fragment?.profiles ?? [];
  const classes = fragment?.classes ?? [];

  const canEdit = permissions?.canEditAdministrationAbsence();

  const { sorting, onSortingChange } = useDefaultSorting();

  const tableData: AbsenceAdministrationTableType[] = useMemo(() => {
    return (
      absences
        .filter((absence) => {
          const profile = profiles.find((p) => p.uuid === absence.owner.uuid);
          return profile?.organizationRoles.includes('STUDENT');
        })
        .map((absence) => {
          const profile = profiles.find((p) => p.uuid === absence.owner.uuid);
          const profileClasses = classes
            .filter((c) => c.membersConnection.edges.some((e) => e.node.uuid === profile?.uuid))
            .filter(onlyUnique)
            .sort((a, b) => naturalStringSort(a.name, b.name));

          return {
            uuid: absence.uuid,
            name: profile?.displayName ?? '',
            classes: profileClasses ?? '',
            reason: absence.reason?.reason ?? '',
            from: dayjs(absence.startTime).format('DD.MM.YY - HH:mm'),
            until: dayjs(absence.startTime).isSame(absence.endTime, 'day')
              ? dayjs(absence.endTime).format('HH:mm')
              : dayjs(absence.endTime).format('DD.MM.YYYY - HH:mm'),
            comment: absence.comment ?? '',
            isExcused: absence.excused ?? false,
            ownerUuid: absence.owner.uuid,
            origin: absence.origin,
            originUuid: absence.originUuid,
          };
        }) ?? []
    );
  }, [absences, classes, profiles]);

  const columns: TableColumns<AbsenceAdministrationTableType>[] = [
    {
      id: 'name',
      accessorKey: 'name',
      header: t('common.name'),
      size: 200,
    },
    {
      id: 'class',
      accessorFn: (row) => row.classes.map((c) => c.shortName).join(', '),
      header: t('classes.title', { count: 2 }),
      cell: ({ row }) => {
        return (
          <Tooltip content={row.original.classes.map((c) => c.name).join(', ')}>
            {row.original.classes.map((c) => c.shortName).join(', ')}
          </Tooltip>
        );
      },
      size: 125,
    },
    {
      id: 'reason',
      accessorKey: 'reason',
      header: t('absences.reasons.title', { context: 'short' }),
      size: 180,
    },
    {
      id: 'from',
      accessorKey: 'from',
      header: t('common.from'),
      size: 130,
      enableGlobalFilter: false,
      enableColumnFilter: false,
    },
    {
      id: 'until',
      accessorKey: 'until',
      header: t('common.until'),
      size: 130,
      enableGlobalFilter: false,
      enableColumnFilter: false,
    },
    {
      id: 'comment',
      accessorKey: 'comment',
      header: t('common.comment'),
      size: 200,
    },
    {
      id: 'origin',
      accessorFn: (absence) => {
        const originUuid = absence.originUuid;
        const createdBy = profiles.find((p) => p.uuid === originUuid)?.displayName;
        return `${createdBy} `;
      },
      header: t('absences.createdBy'),
      cell: ({ row }) => {
        const origin = row.original.origin;
        const originUuid = row.original.originUuid;

        const createdBy = profiles.find((p) => p.uuid === originUuid)?.displayName;
        const originText = getAbsenceOriginTranslation(origin);

        return <Tooltip content={`${createdBy} (${originText})`}>{createdBy}</Tooltip>;
      },
      size: 125,
    },
    {
      id: 'isExcused',
      accessorKey: 'isExcused',
      header: t('absences.isExcused'),
      type: 'boolean',
      size: 75,
    },
  ];

  return (
    <Table<AbsenceAdministrationTableType>
      className={styles['absence-administration-table']}
      data={tableData}
      columns={columns}
      showBorderRadius
      showShadow
      isDesktop={true}
      isOnWhite={false}
      rowSelection={rowSelection}
      onRowSelectionChange={onRowSelectionChange}
      showSelect
      showSort
      sorting={sorting}
      onSortingChange={onSortingChange}
      showActionBar
      bulkEditDropdownContent={(rows: Row<AbsenceAdministrationTableType>[]) => {
        return (
          <DropdownMenu
            usePortal={!!isDesktop}
            data={[
              {
                label: t('absences.excuse'),
                subContent: [
                  {
                    label: t('absences.setExcuse'),
                    onClick: () => {
                      const uuids = rows.filter((r) => !r.original.isExcused).map((r) => r.original.uuid);
                      onExcuse(uuids, true);
                      clearSelection();
                    },
                  },
                  {
                    label: t('absences.cancelExcuse'),
                    onClick: () => {
                      const uuids = rows.filter((r) => r.original.isExcused).map((r) => r.original.uuid);
                      onExcuse(uuids, false);
                      clearSelection();
                    },
                  },
                ],
              },
              {
                type: 'ruler',
              },
              {
                label: t('common.delete'),
                color: 'error',
                onClick: () => {
                  const uuids = rows.map((r) => r.original.uuid);
                  onDelete(uuids);
                  clearSelection();
                },
              },
            ]}
          />
        );
      }}
      actionBarSettings={{
        showExpertFilter: true,
        showAddButton: true,
        showBulkEdit: true,
        bulkEditText: t('common.actionForSelection'),
        disableAddButton: !canEdit,
        extendedActionsRight: (
          <div className={styles.dates}>
            <DatePicker
              onChange={(value) => onFromChange(dayjs(value).startOf('day').toDate())}
              name='from'
              label={t('common.from')}
              value={fromDate}
              maxDate={untilDate}
              minDate={dayjs(currentSchoolYear.start).toDate()}
              className='mr-4'
            />
            <DatePicker
              onChange={(value) => onUntilChange(dayjs(value).endOf('day').toDate())}
              name='until'
              label={t('common.until')}
              value={untilDate}
              minDate={fromDate}
              maxDate={dayjs(currentSchoolYear.end).toDate()}
            />
          </div>
        ),
      }}
      onAddClick={onAdd}
      lastColWidth='68px'
      lastCol={({ original }) => {
        return (
          <Dropdown trigger={<Button icon={<DotsVerticalIcon />} hierarchy='ghost' />}>
            <DropdownMenu
              data={[
                {
                  label: t('common.edit'),
                  onClick: () => onEdit(original.uuid),
                },
                {
                  label: original.isExcused ? t('absences.cancelExcuse') : t('absences.setExcuse'),
                  onClick: () => onExcuse([original.uuid], !original.isExcused),
                },
                {
                  type: 'ruler',
                },
                {
                  label: t('common.delete'),
                  color: 'error',
                  onClick: () => onDelete([original.uuid]),
                },
              ]}
            />
          </Dropdown>
        );
      }}
      loading={isLoading}
    />
  );
};
